import { type FC, type ReactNode, useState } from 'react';
import {
  IconSocialLinkedIn,
  IconSocialFacebook,
  IconSocialTwitter,
  IconCopy,
  Box,
  Text,
  IconTick,
  Hidden,
} from 'braid-design-system';
import { FACEBOOK, TWITTER, LINKEDIN, COPY } from 'src/constants/socialShare';
import handleClickSocialShareLink from 'src/utils/handleClickSocialShareLink';
import type { IJob } from 'src/stores/JobStore/Job';
import {
  trackShareJobOnFacebookEvent,
  trackShareJobOnTwitterEvent,
  trackShareJobOnLinkedInEvent,
  trackCopyAdClickEvent,
} from 'src/utils/events';
import * as styles from './index.css';
import { observer } from 'mobx-react';
import { sleep } from 'src/utils/sleep';
import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';

interface IconClickActionProps {
  Icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}

const IconClickAction = ({
  Icon,
  onClick,
  disabled,
  ...restProps
}: IconClickActionProps) => (
  <Box
    {...restProps}
    onClick={onClick}
    height="touchable"
    width="touchable"
    display="flex"
    alignItems="center"
    justifyContent="center"
    className={styles.option}
    tabIndex={0}
  >
    {Icon}
  </Box>
);

interface Props {
  job: IJob;
}

export const ShareIcons: FC<Props> = ({ job }) => {
  const { t } = useTranslations(translations);
  const { router, site } = useAppConfig();
  const { jobInformation } = job;
  const { title } = jobInformation;

  const jobAdUrl = router.viewJobAd({
    jobStatus: jobInformation.status,
    jobId: jobInformation.jobId ?? 0,
  });

  return (
    <Box display="flex">
      <IconClickAction
        aria-label={t('Share on facebook')}
        Icon={<IconSocialFacebook size="large" />}
        onClick={() => {
          handleClickSocialShareLink({
            option: FACEBOOK,
            jobAdUrl,
            title,
            site,
          });
          trackShareJobOnFacebookEvent();
        }}
        data-testid="share-job-on-fb"
      />
      <IconClickAction
        aria-label={t('Share on twitter')}
        Icon={<IconSocialTwitter size="large" />}
        onClick={() => {
          handleClickSocialShareLink({
            option: TWITTER,
            jobAdUrl,
            title,
            site,
          });
          trackShareJobOnTwitterEvent();
        }}
        data-testid="share-job-on-twtr"
      />
      <IconClickAction
        aria-label={t('Share on linkedIn')}
        Icon={<IconSocialLinkedIn size="large" />}
        onClick={() => {
          handleClickSocialShareLink({
            option: LINKEDIN,
            jobAdUrl,
            title,
            site,
          });
          trackShareJobOnLinkedInEvent();
        }}
        data-testid="share-job-on-li"
      />
      <IconCopyAction
        jobAdUrl={jobAdUrl}
        title={title}
        aria-label={t('Copy job link')}
        data-testid="share-job-link"
      />
    </Box>
  );
};

interface IconCopyActionProps {
  jobAdUrl: string;
  title: string;
}

const IconCopyAction = ({
  jobAdUrl,
  title,
  ...restProps
}: IconCopyActionProps) => {
  const { t } = useTranslations(translations);
  const [copied, setCopied] = useState(false);
  const { site } = useAppConfig();
  return (
    <Box display="flex" alignItems="center">
      <IconClickAction
        {...restProps}
        Icon={
          copied ? (
            <IconTick size="large" tone="positive" />
          ) : (
            <IconCopy size="large" />
          )
        }
        onClick={async () => {
          handleClickSocialShareLink({ option: COPY, jobAdUrl, title, site });
          trackCopyAdClickEvent();
          setCopied(true);
          await sleep(2000);
          setCopied(false);
        }}
      />
      <Hidden below="tablet">
        {copied && (
          <Box paddingLeft="small">
            <Text tone="positive">{t('Copied!')}</Text>
          </Box>
        )}
      </Hidden>
    </Box>
  );
};

export default observer(ShareIcons);
